import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeftIcon,
  CalendarIcon,
  PrinterIcon,
  ShareIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { getProductDetailUrl } from "@/lib/navigation-helpers";

import InquiryStatusBadge from "@/polymet/components/inquiry-status-badge";
import MessageThread, { Message } from "@/polymet/components/message-thread";
import MessageComposer from "@/polymet/components/message-composer";
import { FileAttachment } from "@/polymet/components/file-attachment-preview";

export interface Inquiry {
  id: string;
  subject: string;
  status: "new" | "pending" | "responded" | "closed" | "expired";
  createdAt: string;
  updatedAt: string;
  product: {
    id: string;
    name: string;
    image: string;
  };
  seller: {
    id: string;
    name: string;
    avatar?: string;
  };
  buyer: {
    id: string;
    name: string;
    avatar?: string;
  };
  messages: Message[];
}

interface InquiryDetailProps {
  inquiry: Inquiry;
  currentUserId: string;
  onSendMessage: (
    inquiryId: string,
    message: string,
    attachments: FileAttachment[]
  ) => Promise<void>;
  onStatusChange?: (
    inquiryId: string,
    newStatus: Inquiry["status"]
  ) => Promise<void>;
  onMessageRead?: (messageId: string) => void;
  onBack?: () => void;
  backUrl?: string;
}

export default function InquiryDetail({
  inquiry,
  currentUserId,
  onSendMessage,
  onStatusChange,
  onMessageRead,
  onBack,
  backUrl,
}: InquiryDetailProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isBuyer = currentUserId === inquiry.buyer.id;
  const isSeller = currentUserId === inquiry.seller.id;
  const canReply = inquiry.status !== "closed" && inquiry.status !== "expired";

  const handleSendMessage = async (
    message: string,
    attachments: FileAttachment[]
  ) => {
    try {
      setIsSubmitting(true);
      setError(null);
      await onSendMessage(inquiry.id, message, attachments);
    } catch (err) {
      setError("Failed to send message. Please try again.");
      console.error("Error sending message:", err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus: Inquiry["status"]) => {
    if (onStatusChange) {
      try {
        await onStatusChange(inquiry.id, newStatus);
      } catch (err) {
        console.error("Error changing status:", err);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="space-y-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {backUrl ? (
              <Button variant="ghost" size="sm" asChild>
                <Link to={backUrl}>
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Back
                </Link>
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            <InquiryStatusBadge status={inquiry.status} />
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <PrinterIcon className="h-4 w-4 mr-1" />
              Print
            </Button>
            <Button variant="outline" size="sm">
              <ShareIcon className="h-4 w-4 mr-1" />
              Share
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <CardTitle className="text-xl">{inquiry.subject}</CardTitle>
          <CardDescription className="flex items-center mt-1">
            <CalendarIcon className="h-4 w-4 mr-1" />
            Created on {formatDate(inquiry.createdAt)}
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex flex-col md:flex-row gap-6">
          {/* Product and Participants Info */}
          <div className="w-full md:w-1/3 space-y-4">
            <div className="border rounded-lg p-4">
              <h3 className="font-medium mb-3">Product</h3>
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 rounded-md overflow-hidden bg-muted">
                  <img
                    src={inquiry.product.image}
                    alt={inquiry.product.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="font-medium">{inquiry.product.name}</h4>
                  <Link
                    to={getProductDetailUrl(inquiry.product.id)}
                    className="text-sm text-primary hover:underline"
                  >
                    View Product
                  </Link>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-medium mb-3">Participants</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-muted">
                    {inquiry.buyer.avatar ? (
                      <img
                        src={inquiry.buyer.avatar}
                        alt={inquiry.buyer.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-primary text-primary-foreground">
                        {inquiry.buyer.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{inquiry.buyer.name}</p>
                    <p className="text-sm text-muted-foreground">Buyer</p>
                  </div>
                </div>

                <Separator />

                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-muted">
                    {inquiry.seller.avatar ? (
                      <img
                        src={inquiry.seller.avatar}
                        alt={inquiry.seller.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-primary text-primary-foreground">
                        {inquiry.seller.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{inquiry.seller.name}</p>
                    <p className="text-sm text-muted-foreground">Seller</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Actions - Only show for relevant roles */}
            {(isBuyer || isSeller) &&
              inquiry.status !== "closed" &&
              inquiry.status !== "expired" && (
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium mb-3">Actions</h3>
                  <div className="space-y-2">
                    {isBuyer && inquiry.status === "responded" && (
                      <Button
                        className="w-full"
                        onClick={() => handleStatusChange("closed")}
                      >
                        Close Inquiry
                      </Button>
                    )}

                    {isSeller && inquiry.status === "new" && (
                      <Button
                        className="w-full"
                        onClick={() => handleStatusChange("pending")}
                      >
                        Mark as Pending
                      </Button>
                    )}

                    {isSeller && inquiry.status === "pending" && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleStatusChange("closed")}
                      >
                        Close Inquiry
                      </Button>
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Message Thread */}
          <div className="w-full md:w-2/3 flex flex-col">
            <div className="flex-1 border rounded-lg overflow-hidden">
              <div className="h-[400px] overflow-hidden flex flex-col">
                <MessageThread
                  messages={inquiry.messages}
                  currentUserId={currentUserId}
                  onMessageRead={onMessageRead}
                />
              </div>
            </div>

            {/* Message Composer */}
            {canReply && (
              <div className="mt-4">
                <MessageComposer
                  onSendMessage={handleSendMessage}
                  disabled={isSubmitting}
                  placeholder={`Reply to ${isBuyer ? "seller" : "buyer"}...`}
                />

                {error && (
                  <p className="mt-2 text-sm text-destructive">{error}</p>
                )}
              </div>
            )}

            {!canReply && (
              <div className="mt-4 p-4 bg-muted rounded-lg text-center text-muted-foreground">
                This inquiry is {inquiry.status}. No further replies can be
                sent.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
